import { useRef, useState, useEffect } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (seconds) => {
  if (!seconds || !Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const VideoPlayer = ({ fileId, fileName, aviMeta, serveUrl }) => {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(aviMeta?.duration ?? 0);

  const src = serveUrl || `${API}/files/${fileId}/serve`;
  const fps = aviMeta?.fps;
  const frameCount = aviMeta?.frame_count;

  useEffect(() => {
    setLoading(true);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(aviMeta?.duration ?? 0);
  }, [fileId, aviMeta?.duration]);

  const handleTogglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const handleSeek = ([value]) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = value;
    setCurrentTime(value);
  };

  return (
    <div
      className="flex flex-col h-full min-h-[40vh] bg-[#0a0a0a] rounded-lg overflow-hidden"
      data-testid="video-player"
    >
      <div className="relative flex-1 flex items-center justify-center min-h-0">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}
        <video
          ref={videoRef}
          key={fileId}
          src={src}
          className="max-w-full max-h-full w-auto h-auto"
          onLoadedMetadata={(e) => {
            setDuration(e.currentTarget.duration || aviMeta?.duration || 0);
            setLoading(false);
          }}
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          playsInline
        />
      </div>

      <div className="border-t border-border/20 px-3 sm:px-4 py-3 space-y-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/10"
            onClick={handleTogglePlay}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Slider
            min={0}
            max={duration || 1}
            step={0.01}
            value={[currentTime]}
            onValueChange={handleSeek}
            className="flex-1"
          />
          <span className="text-[10px] sm:text-xs text-muted-foreground tabular-nums whitespace-nowrap">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] sm:text-xs text-muted-foreground">
          {fileName && <span className="truncate max-w-full">{fileName}</span>}
          {fps != null && <span>{fps} fps</span>}
          {frameCount != null && <span>{frameCount} frames</span>}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
